import { Fragment, type ComponentType, type ReactNode } from "react"
import { Box, Icon, Text } from "@chakra-ui/react"
import { FaArrowRightArrowLeft } from "react-icons/fa6"
import { colourVar } from "@hydrologiq/shared/chakra/colourVar"
import type { Connective } from "@hydrologiq/shared/components/alerts"

/** One 40px line in the Condition and Threshold columns. */
export interface ConditionLine<T> {
  key: string
  item: T
  /** Index of the cluster this line belongs to, or null at the top level */
  cluster: number | null
}

/**
 * The lines both columns render, in one order, so each threshold sits beside its clause.
 * Top-level conditions first, then each cluster's.
 */
export function conditionLines<T>(top: T[], clusters: T[][]): ConditionLine<T>[] {
  return [
    ...top.map((item, i) => ({ key: `c${i}`, item, cluster: null })),
    ...clusters.flatMap((items, g) => items.map((item, i) => ({ key: `g${g}c${i}`, item, cluster: g }))),
  ]
}

export const LINE_HEIGHT = 40
export const LINE_GAP = 6
const BRACKET_SPACE = 46
// Editing: room for the chip's swap icon
const EDIT_BRACKET_SPACE = 56
// Editing: how far a group's boundary reaches beyond its lines, above and below. The same as
// the gap between lines, so every gap reads alike: line to line, line to boundary, boundary to
// boundary, and a boundary's own padding
const BOUNDARY_PAD = 6
const ACCENT = "#39aed2"
const TINT = "rgba(57, 174, 210, 0.08)"
// A raw CSS border, so the theme token goes in as its variable
const BOUNDARY_GREY = colourVar("alert.group_boundary")

// Every group while editing: a quiet outline round all its lines
const BOUNDARY = { border: `1.5px dashed ${BOUNDARY_GREY}` }
// A group whose bracket is hovered
const HOVERED = { border: `2px solid ${ACCENT}` }
// A group being dragged into: a clear outline round all its lines, not just a tint
const DROP_ZONE = {
  border: `2px dashed ${ACCENT}`,
  bg: "rgba(57, 174, 210, 0.14)",
}
// A group something just landed in: one pulse of the same outline, settling back to grey; held
// still for people who prefer less motion. Its keyframes are in the shared theme.
const LANDED_PULSE = {
  border: `2px solid ${ACCENT}`,
  animation: "groupLanded 1.1s ease-out forwards",
  "@media (prefers-reduced-motion: reduce)": { animation: "none", background: "rgba(57, 174, 210, 0.14)" },
}

// A group about to be ungrouped: outlined in red while Ungroup is hovered
const DANGER = "#d2042d"
const UNGROUP_PREVIEW = {
  border: `2px dashed ${DANGER}`,
  bg: "rgba(210, 4, 45, 0.06)",
}

// A group just added: holds, then fades over 2.5s, with the editor's glow on its new condition;
// held still for people who prefer less motion. Keyframes in the shared theme.
const ADDED_PULSE = {
  border: `2px solid ${ACCENT}`,
  animation: "groupAdded 2500ms ease-out forwards",
  "@media (prefers-reduced-motion: reduce)": { animation: "none", background: "rgba(57, 174, 210, 0.14)" },
}

const BOUNDARY_STYLE = {
  rest: BOUNDARY,
  hover: HOVERED,
  drop: DROP_ZONE,
  landed: { css: LANDED_PULSE },
  added: { css: ADDED_PULSE },
  ungroup: UNGROUP_PREVIEW,
  error: { border: `2px solid ${DANGER}`, bg: "rgba(210, 4, 45, 0.06)" },
}

/** Which grouping is highlighted: a cluster's index, the whole group, or none. */
export type GroupHighlight = number | "root" | null

const connectiveLabel = (connective: Connective) =>
  connective === "AND" ? "All conditions must be met" : "Any condition can be met"

/** The right-hand bracket and connective chip spanning lines `from`..`to`. */
function Bracket({
  from,
  to,
  y,
  space,
  depth,
  connective,
  highlighted,
  danger = false,
  onToggle,
  onHover,
}: {
  from: number
  to: number
  /** The top of line `i`, in px */
  y: (i: number) => number
  /** Width of each bracket's column, in px */
  space: number
  depth: number
  connective: Connective
  highlighted: boolean
  /** About to be ungrouped: bracket and chip in red */
  danger?: boolean
  /** Editing: the chip flips the group between AND and OR */
  onToggle?: () => void
  /** Editing: hovering the bracket highlights its group */
  onHover?: (hovering: boolean) => void
}) {
  // A one-line group (an empty group's slot) still gets a visible bracket
  const inset = to > from ? 14 : 6
  const top = y(from) + inset
  const height = y(to) - y(from) + LINE_HEIGHT - inset * 2
  const colour = danger ? DANGER : highlighted ? ACCENT : "alert.bracket"
  return (
    <Box
      position="absolute"
      top={`${top}px`}
      h={`${height}px`}
      right={`${depth * space}px`}
      w={`${space}px`}
      pointerEvents={onHover ? "auto" : "none"}
      onMouseEnter={onHover && (() => onHover(true))}
      onMouseLeave={onHover && (() => onHover(false))}
      zIndex={1}
    >
      <Box
        position="absolute"
        top={0}
        bottom={0}
        // The bracket's upright down the middle of its column, so the chip can centre on it
        right={`${space / 2}px`}
        w="12px"
        border="1.5px solid"
        borderColor={colour}
        borderLeft="none"
        borderRadius="0 5px 5px 0"
        transition="border-color 0.15s ease"
        pointerEvents="none"
      />
      {/* Centred on the upright whatever its width, with or without the editing icon */}
      <Box
        position="absolute"
        top="50%"
        right={`${space / 2}px`}
        transform="translate(50%, -50%)"
        display="flex"
        pointerEvents="none"
      >
        <Text
          as={onToggle ? "button" : "span"}
          onClick={onToggle}
          aria-label={onToggle ? `${connectiveLabel(connective)}. Change to ${connective === "AND" ? "OR" : "AND"}` : undefined}
          pointerEvents={onToggle ? "auto" : "none"}
          cursor={onToggle ? "pointer" : undefined}
          fontSize="11px"
          fontWeight="semibold"
          letterSpacing="0.05em"
          color={danger ? DANGER : highlighted ? ACCENT : "primary_light"}
          bg="card"
          border="1px solid"
          borderColor={colour}
          borderRadius="3px"
          px="5px"
          py="1px"
          transition="all 0.15s ease"
          display="inline-flex"
          alignItems="center"
          gap="3px"
        >
          {connective}
          {/* Editing only: shows the chip can be clicked to swap AND and OR */}
          {onToggle && <Icon as={FaArrowRightArrowLeft} boxSize="9px" aria-hidden />}
        </Text>
      </Box>
    </Box>
  )
}

export interface LineContainerProps<T> {
  line: ConditionLine<T>
  highlighted: boolean
  children: ReactNode
}

function DefaultLine<T>({ highlighted, children }: LineContainerProps<T>) {
  return (
    <Box
      h={`${LINE_HEIGHT}px`}
      display="flex"
      alignItems="center"
      gap={2}
      minW={0}
      borderRadius="5px"
      bg={highlighted ? TINT : undefined}
      transition="background-color 0.15s ease"
    >
      {children}
    </Box>
  )
}

/**
 * The Condition column: one line per clause, with a bracket and chip for the whole group
 * and one inside it for each cluster. `renderLine` draws a clause (read or edit). With no
 * connective (a Standard Alert's threshold and time window) the lines get no bracket.
 *
 * Editing draws a boundary round each cluster: grey at rest, blue while hovered, dragged into
 * or just landed in, red only while its Ungroup or an error message about it is hovered. Editing passes `onHover` (a hovered
 * bracket highlights it and its lines), `highlight`
 * (also used while a condition is dragged over a group), `dropTarget`, `landed`,
 * `ungroupPreview` and `LineContainer`.
 */
export function ConditionGroup<T>({
  lines,
  connective,
  clusterConnectives,
  renderLine,
  trailing,
  onToggle,
  highlight = null,
  onHover,
  LineContainer = DefaultLine,
  fit = false,
  dropTarget = null,
  landed = null,
  added = null,
  ungroupPreview = null,
  invalidClusters = [],
  errorClusters = [],
  inBracket = () => true,
}: {
  lines: ConditionLine<T>[]
  connective: Connective | null
  clusterConnectives: Connective[]
  renderLine: (line: ConditionLine<T>) => ReactNode
  /** e.g. "Add condition" while editing */
  trailing?: ReactNode
  /** Editing: flip a connective; `null` is the whole group, a number is that cluster */
  onToggle?: (cluster: number | null) => void
  highlight?: GroupHighlight
  onHover?: (target: GroupHighlight) => void
  LineContainer?: ComponentType<LineContainerProps<T>>
  /** Size to the lines' content, so the brackets sit just after it rather than at the far edge */
  fit?: boolean
  /** Editing: the cluster a condition is being dragged over, outlined as a drop zone */
  dropTarget?: number | null
  /** Editing: the cluster a condition just landed in, which pulses once */
  landed?: number | null
  /** Editing: the cluster just added, highlighted while it fades */
  added?: number | null
  /** Editing: the cluster whose Ungroup is hovered, outlined in red with its bracket */
  ungroupPreview?: number | null
  /** Editing: clusters that can't be saved as they are (too few conditions), bracket in red */
  invalidClusters?: number[]
  /** Editing: clusters an error message is about while it's hovered, outlined in red */
  errorClusters?: number[]
  /** Lines a cluster's bracket spans; the rest (e.g. a group's own row) sit only inside its boundary */
  inBracket?: (line: ConditionLine<T>) => boolean
}) {
  const topItems = lines.filter((l) => l.cluster === null).length + clusterConnectives.length
  const showOuter = connective !== null && topItems > 1
  const clusterRanges = clusterConnectives.map((c, g) => {
    const indexes = lines.flatMap((l, i) => (l.cluster === g ? [i] : []))
    const joined = indexes.filter((i) => inBracket(lines[i]))
    return {
      connective: c,
      from: indexes[0],
      to: indexes[indexes.length - 1],
      bracketFrom: joined[0],
      bracketTo: joined[joined.length - 1],
    }
  })
  const hasClusterBracket = clusterRanges.some((r) => r.from !== undefined && (onHover ? r.to >= r.from : r.to > r.from))
  const depth = hasClusterBracket ? 2 : showOuter ? 1 : 0
  const isHighlighted = (line: ConditionLine<T>) =>
    highlight === "root" || (highlight !== null && line.cluster === highlight)
  // Editing draws a boundary round every group, with room between them
  const editing = onHover !== undefined
  const space = editing ? EDIT_BRACKET_SPACE : BRACKET_SPACE
  const groupStarts = editing ? clusterRanges.flatMap((r) => (r.from === undefined ? [] : [r.from])) : []
  // Extra space before a group's first line: its own boundary's padding, and the padding of the
  // group just before it, if there is one
  const extraBefore = (s: number) => BOUNDARY_PAD + (s > 0 && lines[s - 1].cluster !== null ? BOUNDARY_PAD : 0)
  const y = (i: number) =>
    i * (LINE_HEIGHT + LINE_GAP) + groupStarts.filter((s) => s <= i).reduce((sum, s) => sum + extraBefore(s), 0)
  // With the column's own gap after it, adds `extra` of space
  const spacer = (extra: number) => <Box h={`${extra - LINE_GAP}px`} />
  const boundaryState = (g: number): keyof typeof BOUNDARY_STYLE =>
    g === ungroupPreview ? "ungroup" : errorClusters.includes(g) ? "error" : g === dropTarget ? "drop" : g === landed ? "landed" : g === added ? "added" : highlight === g ? "hover" : "rest"

  return (
    <Box
      role={connective ? "group" : undefined}
      aria-label={connective ? connectiveLabel(connective) : undefined}
      position="relative"
      display="flex"
      flexDirection="column"
      gap={`${LINE_GAP}px`}
      pr={`${depth * space}px`}
      minW={0}
      w={fit ? "fit-content" : undefined}
    >
      {clusterRanges.map((r, g) => {
        if (!editing || r.from === undefined) return null
        const state = boundaryState(g)
        return (
          <Box
            // A landing remounts it, so each one replays the pulse
            key={`zone-${g}-${state === "landed" || state === "added" ? state : "steady"}`}
            data-group-boundary={state}
            data-ungroup-preview={state === "ungroup" || undefined}
            position="absolute"
            top={`${y(r.from) - BOUNDARY_PAD}px`}
            h={`${y(r.to) - y(r.from) + LINE_HEIGHT + BOUNDARY_PAD * 2}px`}
            left="-8px"
            // Round the lines and the group's own bracket
            right={`${space - 4}px`}
            borderRadius="8px"
            pointerEvents="none"
            transition="border-color 0.15s ease, background-color 0.15s ease"
            {...BOUNDARY_STYLE[state]}
          />
        )
      })}
      {lines.map((line, i) => (
        <Fragment key={line.key}>
          {groupStarts.includes(i) && spacer(extraBefore(i))}
          <LineContainer line={line} highlighted={isHighlighted(line)}>
            {renderLine(line)}
          </LineContainer>
        </Fragment>
      ))}
      {showOuter && connective && lines.length > 1 && (
        <Bracket
          from={0}
          to={lines.length - 1}
          y={y}
          space={space}
          depth={0}
          connective={connective}
          highlighted={highlight === "root"}
          onToggle={onToggle && (() => onToggle(null))}
          onHover={onHover && ((on) => onHover(on ? "root" : null))}
        />
      )}
      {clusterRanges.map((r, g) =>
        r.bracketFrom !== undefined && (r.bracketTo > r.bracketFrom || (onHover && r.bracketTo === r.bracketFrom)) ? (
          <Bracket
            key={g}
            from={r.bracketFrom}
            to={r.bracketTo}
            y={y}
            space={space}
            depth={1}
            connective={r.connective}
            highlighted={highlight === g}
            danger={ungroupPreview === g || invalidClusters.includes(g)}
            onToggle={onToggle && (() => onToggle(g))}
            onHover={onHover && ((on) => onHover(on ? g : null))}
          />
        ) : null,
      )}
      {/* Room for the last group's bottom padding before what follows */}
      {groupStarts.length > 0 && spacer(BOUNDARY_PAD)}
      {trailing}
    </Box>
  )
}
